import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Search, User, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LANGUAGE } from '@/features/auth/register.api';
import { GENDER } from '@/features/account-requests/types';
import { useCustomerSearch } from '@/features/customers/customers.api';
import type { Customer } from '@/features/customers/types';
import { cn } from '@/lib/utils';
import {
  AGE_BANDS,
  ageLabel,
  subjectColor,
  useSubjectLabel,
} from './subject-labels';
import {
  MAX_SUBJECTS,
  type GroupFilters,
  type Subject,
} from './usage-analytics.api';

/**
 * Whose visits the report is about.
 *
 * A subject is one person or a group, and the page draws each as its own
 * series. They are compared, not merged, so nothing stops a person from being
 * listed by name and also falling inside a group on the same page.
 */

/**
 * The lowest colour not in use.
 *
 * A slot is taken once and kept: indexing by position instead would hand the
 * second subject the first one's colour the moment the first was removed, and
 * every chart on the page would repaint under a reader mid-comparison.
 */
function freeSlot(subjects: Subject[]) {
  const taken = new Set(subjects.map((subject) => subject.slot));
  for (let slot = 0; slot < MAX_SUBJECTS; slot++) {
    if (!taken.has(slot)) return slot;
  }
  return 0;
}

function newKey() {
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** The colour key beside a subject's name, wherever one is named. */
export function SubjectDot({
  subject,
  className,
}: {
  subject: Subject;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn('inline-block size-2.5 shrink-0 rounded-full', className)}
      style={{ backgroundColor: subjectColor(subject) }}
    />
  );
}

const ANY = 'any';

function PersonPicker({
  taken,
  onPick,
}: {
  taken: Set<string>;
  onPick: (customer: Customer) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  // Deferred rather than debounced by hand: the box stays responsive and the
  // request follows the last thing typed, which is what a lookup by email is.
  const term = useDeferredValue(draft);
  const search = useCustomerSearch(term.trim().length >= 3 ? term : '');
  const results = search.data?.results ?? [];

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          autoFocus
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t('usage.personPlaceholder')}
          aria-label={t('usage.personPlaceholder')}
          className="pl-9"
        />
        {search.isFetching && (
          <Loader2 className="text-muted-foreground absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin" />
        )}
      </div>

      {draft.trim().length < 3 ? (
        <p className="text-muted-foreground text-sm">{t('usage.personHint')}</p>
      ) : search.isError ? (
        <p className="text-destructive text-sm">{t('usage.personFailed')}</p>
      ) : search.isSuccess && results.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('usage.personNone')}</p>
      ) : (
        <ul className="divide-y rounded-lg border">
          {results.slice(0, 25).map((customer) => {
            const name = [customer.firstName, customer.lastName]
              .filter(Boolean)
              .join(' ');
            const added = taken.has(customer.id);
            return (
              <li key={customer.id}>
                <button
                  type="button"
                  disabled={added}
                  onClick={() => onPick(customer)}
                  className="hover:bg-accent/50 focus-visible:bg-accent/50 flex w-full items-center gap-3 px-3 py-2 text-left outline-none disabled:opacity-50"
                >
                  <User className="text-muted-foreground size-4 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {name || customer.email || t('usage.unnamed')}
                    </span>
                    {name && customer.email && (
                      <span className="text-muted-foreground block truncate text-xs">
                        {customer.email}
                      </span>
                    )}
                  </span>
                  {added && (
                    <span className="text-muted-foreground text-xs">
                      {t('usage.alreadyAdded')}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function FilterSelect({
  id,
  label,
  value,
  onChange,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-muted-foreground text-xs">
        {label}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function GroupForm({ onAdd }: { onAdd: (filters: GroupFilters) => void }) {
  const { t } = useTranslation();
  const [gender, setGender] = useState(ANY);
  const [age, setAge] = useState(ANY);
  const [language, setLanguage] = useState(ANY);
  const [account, setAccount] = useState(ANY);
  const [joinedFrom, setJoinedFrom] = useState('');
  const [joinedTo, setJoinedTo] = useState('');

  const any = { value: ANY, label: t('usage.any') };
  const band = AGE_BANDS.find((entry) => entry.id === age);
  const unfiltered =
    [gender, age, language, account].every(
      (value) => value === ANY
    ) &&
    !joinedFrom &&
    !joinedTo;
  const joinedInvalid = Boolean(joinedFrom && joinedTo) && joinedFrom > joinedTo;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <FilterSelect
          id="usage-gender"
          label={t('usage.gender')}
          value={gender}
          onChange={setGender}
          options={[
            any,
            { value: String(GENDER.female), label: t('usage.female') },
            { value: String(GENDER.male), label: t('usage.male') },
            { value: String(GENDER.notSaid), label: t('usage.genderNotSaid') },
          ]}
        />
        <FilterSelect
          id="usage-age"
          label={t('usage.ageGroup')}
          value={age}
          onChange={setAge}
          options={[
            any,
            ...AGE_BANDS.map((entry) => ({
              value: entry.id,
              label: ageLabel(entry.min, entry.max) ?? entry.id,
            })),
          ]}
        />
        <FilterSelect
          id="usage-language"
          label={t('common.language')}
          value={language}
          onChange={setLanguage}
          options={[
            any,
            { value: String(LANGUAGE.en), label: t('common.english') },
            { value: String(LANGUAGE.es), label: t('common.spanish') },
          ]}
        />
        <FilterSelect
          id="usage-account"
          label={t('usage.accountType')}
          value={account}
          onChange={setAccount}
          options={[
            any,
            { value: 'registered', label: t('usage.registered') },
            { value: 'guest', label: t('usage.guests') },
          ]}
        />
        <div className="space-y-1">
          <Label htmlFor="usage-joined-from" className="text-muted-foreground text-xs">
            {t('usage.joinedFrom')}
          </Label>
          <Input
            id="usage-joined-from"
            type="date"
            value={joinedFrom}
            onChange={(event) => setJoinedFrom(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="usage-joined-to" className="text-muted-foreground text-xs">
            {t('usage.joinedTo')}
          </Label>
          <Input
            id="usage-joined-to"
            type="date"
            value={joinedTo}
            aria-invalid={joinedInvalid}
            onChange={(event) => setJoinedTo(event.target.value)}
          />
        </div>
      </div>

      {joinedInvalid && (
        <p className="text-destructive text-xs">{t('usage.rangeInvalid')}</p>
      )}

      {/* Said before the button rather than after it: an age filter quietly
          drops everybody who never gave a birth date, and a group that looks
          small for that reason reads as a finding about the age band. */}
      {age !== ANY && (
        <p className="text-muted-foreground text-xs">{t('usage.ageCaveat')}</p>
      )}

      <Button
        type="button"
        className="w-full"
        disabled={joinedInvalid}
        onClick={() =>
          onAdd({
            gender: gender === ANY ? undefined : Number(gender),
            ageMin: band?.min,
            ageMax: band?.max,
            language: language === ANY ? undefined : Number(language),
            guest: account === ANY ? undefined : account === 'guest',
            registeredFrom: joinedFrom || undefined,
            registeredTo: joinedTo || undefined,
          })
        }
      >
        {unfiltered ? t('usage.addEveryone') : t('usage.addGroup')}
      </Button>
    </div>
  );
}

export function SubjectBuilder({
  subjects,
  onChange,
  disabled,
}: {
  subjects: Subject[];
  onChange: (subjects: Subject[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const label = useSubjectLabel();
  const [open, setOpen] = useState(false);

  const full = subjects.length >= MAX_SUBJECTS;
  const takenUsers = new Set(
    subjects.flatMap((subject) =>
      subject.kind === 'person' ? [subject.userId] : []
    )
  );

  const add = (subject: Subject) => {
    onChange([...subjects, subject]);
    setOpen(false);
  };

  return (
    <section className="bg-card space-y-2 rounded-xl border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-muted-foreground mr-1 text-xs font-semibold tracking-wider uppercase">
          {t('usage.subjects')}
        </h3>

        {subjects.map((subject) => (
          <span
            key={subject.key}
            className="bg-background inline-flex max-w-full items-center gap-2 rounded-full border py-1 pr-1 pl-2.5 text-sm"
          >
            <SubjectDot subject={subject} />
            {subject.kind === 'person' ? (
              <User className="text-muted-foreground size-3.5 shrink-0" />
            ) : (
              <Users className="text-muted-foreground size-3.5 shrink-0" />
            )}
            <span className="truncate">{label(subject)}</span>
            <button
              type="button"
              disabled={disabled}
              aria-label={t('usage.removeSubject', { name: label(subject) })}
              onClick={() =>
                onChange(subjects.filter((entry) => entry.key !== subject.key))
              }
              className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring/50 rounded-full p-1 outline-none focus-visible:ring-2 disabled:opacity-50"
            >
              <X className="size-3.5" />
            </button>
          </span>
        ))}

        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || full}
          onClick={() => setOpen(true)}
        >
          <Plus />
          {t('usage.addSubject')}
        </Button>
      </div>

      <p className="text-muted-foreground text-xs">
        {full
          ? t('usage.subjectsFull', { count: MAX_SUBJECTS })
          : t('usage.subjectsHelp')}
      </p>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle>{t('usage.addSubject')}</SheetTitle>
            <SheetDescription>{t('usage.addSubjectHelp')}</SheetDescription>
          </SheetHeader>

          <Tabs defaultValue="person" className="p-4">
            <TabsList>
              <TabsTrigger value="person">
                <User />
                {t('usage.person')}
              </TabsTrigger>
              <TabsTrigger value="group">
                <Users />
                {t('usage.group')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="person">
              <PersonPicker
                taken={takenUsers}
                onPick={(customer) =>
                  add({
                    kind: 'person',
                    key: newKey(),
                    slot: freeSlot(subjects),
                    userId: customer.id,
                    name: [customer.firstName, customer.lastName]
                      .filter(Boolean)
                      .join(' '),
                    email: customer.email,
                  })
                }
              />
            </TabsContent>

            <TabsContent value="group">
              <GroupForm
                onAdd={(filters) =>
                  add({
                    kind: 'group',
                    key: newKey(),
                    slot: freeSlot(subjects),
                    ...filters,
                  })
                }
              />
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>
    </section>
  );
}
